export type ProtectedDocumentMode = 'preview' | 'download';

const responseMessage = async (response: Response) => {
  const body = (await response.json().catch(() => null)) as { message?: string } | null;
  return body?.message ?? `No se pudo obtener el documento (HTTP ${response.status}).`;
};

const fileNameFrom = (response: Response, fallback: string) => {
  const disposition = response.headers.get('content-disposition') ?? '';
  const utf8 = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const plain = disposition.match(/filename="?([^";]+)"?/i)?.[1];
  try {
    return decodeURIComponent(utf8 ?? plain ?? fallback);
  } catch {
    return plain ?? fallback;
  }
};

export async function fetchProtectedDocument(
  url: string,
  fallbackName = 'documento',
  signal?: AbortSignal,
) {
  const response = await fetch(url, {
    credentials: 'include',
    headers: { Accept: 'application/pdf,image/*,application/octet-stream' },
    signal: signal ?? null,
  });
  if (!response.ok) throw new Error(await responseMessage(response));
  return { blob: await response.blob(), fileName: fileNameFrom(response, fallbackName) };
}

export async function openProtectedDocument(
  url: string,
  mode: ProtectedDocumentMode,
  fallbackName = 'documento',
) {
  const preview = mode === 'preview' ? window.open('about:blank', '_blank') : null;
  if (preview) {
    preview.opener = null;
    preview.document.title = 'Cargando documento…';
    preview.document.body.textContent = 'Cargando documento protegido…';
  }
  try {
    const { blob, fileName } = await fetchProtectedDocument(url, fallbackName);
    const objectUrl = URL.createObjectURL(blob);
    if (mode === 'download') {
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } else if (preview) {
      preview.location.replace(objectUrl);
    } else {
      const link = document.createElement('a');
      link.href = objectUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      link.remove();
    }
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
  } catch (error) {
    preview?.close();
    throw error;
  }
}
